import {
  getConsentSnapshot,
  subscribeToConsentChanges,
  type ConsentSnapshot,
} from '@services/consent';

const GOOGLE_ADS_TAG_ID = 'AW-17791132671';
const GOOGLE_TAG_SCRIPT_ID = 'sendallfiles-google-ads-tag';
const GOOGLE_TAG_GATEWAY_PATH = '/safgtw/';
const GOOGLE_TAG_PRODUCTION_ROOT_HOST = 'sendallfiles.com';
const GOOGLE_TAG_SCRIPT_LOAD_TIMEOUT_MS = 15_000;
const GOOGLE_TAG_SCRIPT_RETRY_DELAYS_MS = [1_000, 5_000] as const;

// Temporary Advanced Consent Mode window. Europe/Zurich is UTC+02:00 on this date. The branch
// automatically becomes inactive at midnight on 1 September 2026; keep this single deadline as
// the removal point after the experiment.
export const TEMPORARY_ADVANCED_CONSENT_MODE_EXPIRES_AT = Date.parse(
  '2026-09-01T00:00:00+02:00',
);

type ConsentState = 'granted' | 'denied';
type Gtag = (...args: unknown[]) => void;
export type GoogleTagLoadState = 'not_started' | 'loading' | 'loaded' | 'failed';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let tagStarted = false;
let tagLoadState: GoogleTagLoadState = 'not_started';
let tagLoadAttempts = 0;
let tagLoadTimeout: ReturnType<typeof setTimeout> | null = null;
let tagRetryTimeout: ReturnType<typeof setTimeout> | null = null;
let tagLoaderGeneration = 0;
let tagScriptUrl: string | null = null;
let unsubscribeFromConsent: (() => void) | null = null;

function optionalTelemetryIsDisabled(): boolean {
  return import.meta.env.VITE_DISABLE_OPTIONAL_TELEMETRY === 'true';
}

export function isTemporaryAdvancedConsentModeActive(now = Date.now()): boolean {
  return now < TEMPORARY_ADVANCED_CONSENT_MODE_EXPIRES_AT;
}

interface GoogleTagScriptUrlOptions {
  origin: string;
  isProduction: boolean;
  scriptUrlOverride?: string;
}

function isTrustedProductionHost(hostname: string): boolean {
  const normalized = hostname.trim().toLowerCase().replace(/\.$/, '');
  return (
    normalized === GOOGLE_TAG_PRODUCTION_ROOT_HOST ||
    normalized.endsWith(`.${GOOGLE_TAG_PRODUCTION_ROOT_HOST}`)
  );
}

/**
 * Builds the loader URL without reading browser globals so production, localhost and tests have a
 * deterministic contract. Cloudflare's gateway serves the configured standalone tag directly at
 * the measurement path, so the production URL must not append Google's `/gtag/js?id=...` suffix.
 * VITE_GOOGLE_TAG_SCRIPT_URL is an explicit operational escape hatch and is treated as the complete
 * loader URL.
 */
export function buildGoogleTagScriptUrl({
  origin,
  isProduction,
  scriptUrlOverride,
}: GoogleTagScriptUrlOptions): string | null {
  const configuredUrl = scriptUrlOverride?.trim();
  if (configuredUrl) {
    return new URL(configuredUrl, origin).toString();
  }

  const siteUrl = new URL(origin);
  if (isProduction && isTrustedProductionHost(siteUrl.hostname)) {
    return new URL(GOOGLE_TAG_GATEWAY_PATH, siteUrl).toString();
  }

  // Production previews (for example, pages.dev) and local development must never pollute the
  // live Ads account. Tests and deliberate non-production diagnostics can opt in with the override.
  return null;
}

function currentGoogleTagScriptUrl(): string | null {
  return buildGoogleTagScriptUrl({
    origin: window.location.origin,
    isProduction: import.meta.env.PROD,
    scriptUrlOverride: import.meta.env.VITE_GOOGLE_TAG_SCRIPT_URL,
  });
}

function getOrCreateGtag(): Gtag {
  window.dataLayer = window.dataLayer ?? [];
  if (typeof window.gtag !== 'function') {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer?.push(args);
    };
  }
  return window.gtag;
}

function clearTagLoadTimeout(): void {
  if (tagLoadTimeout !== null) {
    clearTimeout(tagLoadTimeout);
    tagLoadTimeout = null;
  }
}

function clearTagRetryTimeout(): void {
  if (tagRetryTimeout !== null) {
    clearTimeout(tagRetryTimeout);
    tagRetryTimeout = null;
  }
}

function appendGoogleTagScript(scriptUrl: string, generation: number): void {
  if (generation !== tagLoaderGeneration || typeof document === 'undefined') {
    return;
  }

  document.getElementById(GOOGLE_TAG_SCRIPT_ID)?.remove();
  clearTagLoadTimeout();
  clearTagRetryTimeout();

  const script = document.createElement('script');
  script.id = GOOGLE_TAG_SCRIPT_ID;
  script.async = true;
  script.src = scriptUrl;
  // The gateway is same-origin, so the browser would otherwise attach the complete landing URL
  // (including a possible click ID) before Google's in-tag redaction has had a chance to run.
  script.referrerPolicy = 'no-referrer';

  tagLoadAttempts += 1;
  tagLoadState = 'loading';
  let attemptFinished = false;

  const finishAttempt = (loaded: boolean) => {
    if (attemptFinished || generation !== tagLoaderGeneration) {
      return;
    }
    attemptFinished = true;
    clearTagLoadTimeout();

    if (loaded) {
      tagLoadState = 'loaded';
      return;
    }

    script.remove();
    const retryDelay = GOOGLE_TAG_SCRIPT_RETRY_DELAYS_MS[tagLoadAttempts - 1];
    if (retryDelay === undefined) {
      tagLoadState = 'failed';
      return;
    }

    // Commands already accepted by the gtag stub remain in dataLayer and are processed when a
    // retry succeeds. Keep the state as loading so callers can continue to queue during the short
    // retry window instead of losing one-shot conversion signals.
    tagLoadState = 'loading';
    tagRetryTimeout = setTimeout(() => {
      tagRetryTimeout = null;
      appendGoogleTagScript(scriptUrl, generation);
    }, retryDelay);
  };

  script.addEventListener('load', () => finishAttempt(true), { once: true });
  script.addEventListener('error', () => finishAttempt(false), { once: true });
  tagLoadTimeout = setTimeout(
    () => finishAttempt(false),
    GOOGLE_TAG_SCRIPT_LOAD_TIMEOUT_MS,
  );
  document.head.appendChild(script);
}

function consentSignals(snapshot: ConsentSnapshot): Record<string, ConsentState> {
  const adsGranted = snapshot.hasDecision && snapshot.preferences.adsMeasurement;
  const analyticsGranted = snapshot.hasDecision && snapshot.preferences.analytics;

  return {
    ad_storage: adsGranted ? 'granted' : 'denied',
    ad_user_data: adsGranted ? 'granted' : 'denied',
    // SendAllFiles does not use consent for personalised advertising. This remains denied even
    // when the user enables aggregate advertising measurement.
    ad_personalization: 'denied',
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
  };
}

function sendConsentUpdate(snapshot: ConsentSnapshot): void {
  if (!tagStarted || typeof window.gtag !== 'function') {
    return;
  }
  const signals = consentSignals(snapshot);
  window.gtag('consent', 'update', signals);
  if (signals.ad_user_data === 'denied') {
    // Manual enhanced-conversion user_data is a global gtag setting. Clear any queued identity on
    // withdrawal even though each conversion also brackets its own short-lived set/clear pair.
    window.gtag('set', 'user_data', null);
  }
}

function startGoogleAdsTag(snapshot: ConsentSnapshot): void {
  if (
    tagStarted ||
    optionalTelemetryIsDisabled() ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return;
  }

  const scriptUrl = currentGoogleTagScriptUrl();
  if (!scriptUrl) {
    return;
  }

  // Consent defaults must be queued before every config command. During the temporary Advanced
  // Consent Mode window this also runs without a decision or after a rejection, but all four
  // signals remain denied and Google is responsible for reducing subsequent conversion commands
  // to consent-aware, cookieless pings.
  const gtag = getOrCreateGtag();
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
  // When ad storage is denied, instruct Google to redact click identifiers from both request
  // parameters and page URLs. URL passthrough is intentionally not enabled.
  gtag('set', 'ads_data_redaction', true);
  if (snapshot.hasDecision) {
    gtag('consent', 'update', consentSignals(snapshot));
  }
  gtag('js', new Date());
  gtag('config', GOOGLE_ADS_TAG_ID, {
    allow_ad_personalization_signals: false,
  });

  tagStarted = true;
  tagScriptUrl = scriptUrl;
  tagLoaderGeneration += 1;
  appendGoogleTagScript(scriptUrl, tagLoaderGeneration);
}

function applyConsent(snapshot: ConsentSnapshot): void {
  if (optionalTelemetryIsDisabled()) {
    if (tagStarted && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
      });
      window.gtag('set', 'user_data', null);
    }
    return;
  }

  if (isTemporaryAdvancedConsentModeActive()) {
    if (!tagStarted) {
      startGoogleAdsTag(snapshot);
    } else {
      sendConsentUpdate(snapshot);
    }
    return;
  }

  if (snapshot.hasDecision && snapshot.preferences.adsMeasurement) {
    if (!tagStarted) {
      startGoogleAdsTag(snapshot);
    } else {
      sendConsentUpdate(snapshot);
    }
    return;
  }

  // If consent is withdrawn after the tag has loaded, notify Google once and prevent all future
  // product/conversion events at their call sites. We deliberately do not reload or replay events.
  sendConsentUpdate(snapshot);
}

/**
 * Applies a stored decision synchronously and then follows preference changes for this page load.
 * Calling this function more than once is safe.
 */
export function initializeGoogleTags(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  if (unsubscribeFromConsent) {
    return;
  }

  applyConsent(getConsentSnapshot());
  unsubscribeFromConsent = subscribeToConsentChanges(applyConsent);
}

/**
 * True when conversion commands may be queued under either a real ads-measurement grant or the
 * temporary Advanced Consent Mode window. Callers must still avoid supplying identifiers unless
 * the first-party consent snapshot is genuinely granted.
 */
export function canQueueGoogleAdsEvent(): boolean {
  const snapshot = getConsentSnapshot();
  const adsMeasurementGranted =
    snapshot.hasDecision && snapshot.preferences.adsMeasurement;
  return Boolean(
    !optionalTelemetryIsDisabled() &&
      (adsMeasurementGranted || isTemporaryAdvancedConsentModeActive()) &&
      tagStarted &&
      tagLoadState !== 'failed' &&
      typeof window !== 'undefined' &&
      typeof window.gtag === 'function',
  );
}

/** Returns transport readiness for privacy-safe diagnostics; it contains no user information. */
export function getGoogleTagLoadState(): GoogleTagLoadState {
  return tagLoadState;
}

/**
 * Re-arms a loader that exhausted its bounded startup retries. Conversion call sites invoke this
 * before checking readiness so a transient outage cannot suppress every later event on a
 * long-lived page. Consent and the administrative telemetry switch are checked again first.
 */
export function recoverGoogleTagLoaderForEvent(): void {
  if (
    tagLoadState !== 'failed' ||
    !tagStarted ||
    !tagScriptUrl ||
    optionalTelemetryIsDisabled() ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return;
  }

  const snapshot = getConsentSnapshot();
  const mayMeasure =
    (snapshot.hasDecision && snapshot.preferences.adsMeasurement) ||
    isTemporaryAdvancedConsentModeActive();
  if (!mayMeasure) {
    return;
  }

  tagLoadAttempts = 0;
  tagLoaderGeneration += 1;
  appendGoogleTagScript(tagScriptUrl, tagLoaderGeneration);
}

/** Test-only reset for module state; not used by production code. */
export function resetGoogleTagsForTests(): void {
  unsubscribeFromConsent?.();
  unsubscribeFromConsent = null;
  tagLoaderGeneration += 1;
  clearTagLoadTimeout();
  clearTagRetryTimeout();
  tagStarted = false;
  tagLoadState = 'not_started';
  tagLoadAttempts = 0;
  tagScriptUrl = null;
}
